/**
 * Where the holes on one part meet the holes on another (#693).
 *
 * Seams that trace the same construction line at the same pitch share a hole
 * grid (#199 Phase 3), so where the parts overlap they ALL punch holes at the
 * same points. That is the information you need to sew them together — and
 * cutting the parts out is exactly what destroys it, since the overlap was the
 * only thing showing which hole met which.
 *
 * So each place where seams meet gets a LABELLED mark, the same letter on every
 * seam that has that hole. Put the holes marked A together and the rows run in
 * step from there — one grid at one pitch, so every hole after it lands on its
 * partner too.
 *
 * A per-seam "here is where this row starts" ring cannot do this job: the rows
 * are clipped to their own parts and generally begin at different points along
 * the shared line, so their first holes are not partners.
 *
 * Sharing is a GROUP property, not a pairwise one — three parts can overlap at
 * one hole. So the unit is a JOINT: a maximal set of seams that coincide, marked
 * once at the first hole where that particular set meets. Two parts overlapping,
 * then a third joining further along, is three joints (A+B, then A+B+C, then
 * B+C once A has ended) — each its own letter, each marked once.
 *
 * One computation, three consumers that must agree: the canvas renderer, the
 * SVG/PDF pattern, and the written assembly steps.
 */
import type paper from "paper";
import {
  pieceStitchLines,
  type Piece,
  type Project,
  type Vec2,
} from "./Project";
import { gridGroupKey } from "./constructionStitchGrid";
import { placedStitchHoles } from "./placedStitchHoles";

/** How close two holes must be to count as the same hole. They are placed from
 *  the same construction arc at the same pitch, so a genuine shared hole agrees
 *  to floating-point noise; this is far tighter than a pitch and far looser than
 *  the error. */
const SHARED_HOLE_TOL_MM = 0.05;

/** One seam's end of a joint — which hole, on which seam, of which part. */
export interface JointEnd {
  pieceId: string;
  pieceName: string;
  stitchId: string;
  stitchName: string;
  /** Index into the seam's ON-LEATHER placements (`placedStitchHoles`), which is
   *  the list every consumer draws from. Not a raw placement index: a seam
   *  overhanging its part must mark a hole it actually punches. */
  index: number;
}

/** A hole several seams share, and who shares it. */
export interface StitchJoint {
  /** Printed beside every member's ring, and named in the assembly steps. */
  label: string;
  /** Two or more, in project order. */
  ends: JointEnd[];
  /** Project-space position of the shared hole — the SAME point for every end,
   *  which is what makes them partners. */
  position: Vec2;
}

/** Labels run A, B, … Z, then AA. Letters rather than digits on purpose: the
 *  assembly steps have to say things like "the 5th hole, marked A", and a
 *  numbered mark there reads as a second ordinal. */
export function jointLabelAt(i: number): string {
  let n = i;
  let out = "";
  do {
    out = String.fromCharCode(65 + (n % 26)) + out;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return out;
}

interface SeamHoles {
  piece: Piece;
  stitchId: string;
  stitchName: string;
  key: string;
  positions: Vec2[];
}

/**
 * Every joint in the project, in a stable order.
 *
 * Only seams in the SAME grid group are considered — same source construction
 * line, same pitch. Two parts that merely happen to overlap are not sewn
 * together by that fact, and pairing them would put marks on unrelated seams.
 */
export function stitchJoints(
  project: Project,
  scope: paper.PaperScope,
): StitchJoint[] {
  const seams: SeamHoles[] = [];
  for (const piece of project.pieces) {
    for (const line of pieceStitchLines(piece)) {
      // In a grid group at all? The leader carries `gridMain` and no phase.
      if (line.gridPhase == null && line.gridMain !== true) continue;
      const key = gridGroupKey(line);
      if (!key) continue;
      seams.push({
        piece,
        stitchId: line.id,
        stitchName: line.name ?? "seam",
        key,
        positions: placedStitchHoles(line, piece, scope).map((p) => p.position),
      });
    }
  }
  if (seams.length < 2) return [];

  const joints: StitchJoint[] = [];
  // One mark per distinct set of seams that meet. Scanning seam-by-seam then
  // hole-by-hole makes the FIRST hole of each set the marked one, in a stable
  // order that doesn't depend on floating-point comparison order.
  const seen = new Set<string>();
  for (let i = 0; i < seams.length; i++) {
    const a = seams[i]!;
    for (let ia = 0; ia < a.positions.length; ia++) {
      const at = a.positions[ia]!;
      // Who else punches this exact hole?
      const ends: JointEnd[] = [];
      for (let j = 0; j < seams.length; j++) {
        const b = seams[j]!;
        if (b.key !== a.key) continue;
        const ib =
          j === i ? ia : b.positions.findIndex((p) => samePoint(p, at));
        if (ib < 0) continue;
        ends.push({
          pieceId: b.piece.id,
          pieceName: b.piece.name ?? "part",
          stitchId: b.stitchId,
          stitchName: b.stitchName,
          index: ib,
        });
      }
      if (ends.length < 2) continue;
      const signature = ends
        .map((e) => e.stitchId)
        .sort()
        .join("|");
      if (seen.has(signature)) continue;
      seen.add(signature);
      joints.push({ label: jointLabelAt(joints.length), ends, position: at });
    }
  }
  return joints;
}

function samePoint(p: Vec2, q: Vec2): boolean {
  return (
    Math.abs(p.x - q.x) < SHARED_HOLE_TOL_MM &&
    Math.abs(p.y - q.y) < SHARED_HOLE_TOL_MM
  );
}

/** The joint marks each seam carries, keyed by stitch id — what a renderer
 *  needs without caring who the partners are. */
export function jointMarksByStitch(
  joints: StitchJoint[],
): Map<string, Array<{ index: number; label: string }>> {
  const out = new Map<string, Array<{ index: number; label: string }>>();
  for (const j of joints) {
    for (const end of j.ends) {
      const list = out.get(end.stitchId) ?? [];
      list.push({ index: end.index, label: j.label });
      out.set(end.stitchId, list);
    }
  }
  return out;
}
